import React from 'react';
import '../styles/LegendPanel.css';

const MAJOR_KEYS = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];
const MINOR_KEYS = ['Am', 'Em', 'Bm', 'F#m', 'C#m', 'G#m', 'D#m', 'Bbm', 'Fm', 'Cm', 'Gm', 'Dm'];

function keyIsMinor(key) { return /m$|minor/i.test(String(key || '')); }
function normalizeKey(key) { return String(key || '').replace(/♭/g, 'b').replace(/♯/g, '#').trim(); }
const SIZE = 300;
const CENTER = SIZE / 2;
const CENTER_RADIUS = 46;
const INNER_RADIUS = 88;
const OUTER_RADIUS = 138;
const toPoint = (radius, angle) => {
  const radians = (angle * Math.PI) / 180;
  return [CENTER + radius * Math.cos(radians), CENTER + radius * Math.sin(radians)];
};
const sectorPath = (innerRadius, outerRadius, index) => {
  const centerAngle = -90 + index * 30;
  const startAngle = centerAngle - 15;
  const endAngle = centerAngle + 15;
  const [outerStartX, outerStartY] = toPoint(outerRadius, startAngle);
  const [outerEndX, outerEndY] = toPoint(outerRadius, endAngle);
  const [innerEndX, innerEndY] = toPoint(innerRadius, endAngle);
  const [innerStartX, innerStartY] = toPoint(innerRadius, startAngle);
  return `M ${innerStartX} ${innerStartY} L ${outerStartX} ${outerStartY} A ${outerRadius} ${outerRadius} 0 0 1 ${outerEndX} ${outerEndY} L ${innerEndX} ${innerEndY} A ${innerRadius} ${innerRadius} 0 0 0 ${innerStartX} ${innerStartY} Z`;
};

export function Circleof5thPanel({ selectedKey = 'C', baseKey, onSelect }) {
  const currentKey = normalizeKey(selectedKey);
  const normalizedBaseKey = normalizeKey(baseKey || selectedKey);
  const minor = keyIsMinor(currentKey);
  const baseIsMinor = keyIsMinor(normalizedBaseKey);
  const selectedIndex = Math.max(0, (minor ? MINOR_KEYS : MAJOR_KEYS).findIndex((key) => key === currentKey));
  const baseIndex = Math.max(0, (baseIsMinor ? MINOR_KEYS : MAJOR_KEYS).findIndex((key) => key === normalizedBaseKey));
  const selectKey = (key) => onSelect?.(key);
  const handleKeyDown = (event, key) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectKey(key);
    }
  };
  return (
    <div className="circle-fifths-panel">
      <svg className="fifths-wheel" viewBox={`0 0 ${SIZE} ${SIZE}`} role="group" aria-label="Circle of fifths">
        <circle cx={CENTER} cy={CENTER} r={OUTER_RADIUS} className="fifths-ring fifths-ring--outer" />
        <circle cx={CENTER} cy={CENTER} r={INNER_RADIUS} className="fifths-ring fifths-ring--inner" />
        {Array.from({ length: 12 }).map((_, index) => {
          const [x1, y1] = toPoint(CENTER_RADIUS, -105 + index * 30);
          const [x2, y2] = toPoint(OUTER_RADIUS, -105 + index * 30);
          return <line key={`line-${index}`} x1={x1} y1={y1} x2={x2} y2={y2} className="fifths-divider" />;
        })}
        <path className="fifths-base-selection" d={sectorPath(baseIsMinor ? CENTER_RADIUS : INNER_RADIUS, baseIsMinor ? INNER_RADIUS : OUTER_RADIUS, baseIndex)} />
        {currentKey !== normalizedBaseKey && (
          <path className="fifths-selection" d={sectorPath(minor ? CENTER_RADIUS : INNER_RADIUS, minor ? INNER_RADIUS : OUTER_RADIUS, selectedIndex)} />
        )}
        {MAJOR_KEYS.map((key, index) => {
          const [x, y] = toPoint(115, -90 + index * 30);
          return (
            <g key={`major-${key}`} className="fifths-key" role="button" tabIndex="0" onClick={() => selectKey(key)} onKeyDown={(event) => handleKeyDown(event, key)}>
              <path d={sectorPath(INNER_RADIUS, OUTER_RADIUS, index)} className="fifths-key-hitbox" />
              <text x={x} y={y} className={`fifths-major ${!baseIsMinor && normalizedBaseKey === key ? 'base' : ''} ${!minor && currentKey === key ? 'current' : ''}`}>{key}</text>
            </g>
          );
        })}
        {MINOR_KEYS.map((key, index) => {
          const [x, y] = toPoint(68, -90 + index * 30);
          return (
            <g key={`minor-${key}`} className="fifths-key" role="button" tabIndex="0" onClick={() => selectKey(key)} onKeyDown={(event) => handleKeyDown(event, key)}>
              <path d={sectorPath(CENTER_RADIUS, INNER_RADIUS, index)} className="fifths-key-hitbox" />
              <text x={x} y={y} className={`fifths-minor ${baseIsMinor && normalizedBaseKey === key ? 'base' : ''} ${minor && currentKey === key ? 'current' : ''}`}>{key}</text>
            </g>
          );
        })}
        <circle cx={CENTER} cy={CENTER} r={CENTER_RADIUS} className="fifths-center" />
        <text x={CENTER} y={CENTER + 4} className="fifths-center-label">KEY</text>
      </svg>
    </div>
  );
}

export default function Circleof5thPanelLegacy({ selectedKey, position = 'right' }) {
  return <aside className={`legend-fixed legend-fixed--${position} legend-fixed--circle`} aria-label="Circle of Fifth"><Circleof5thPanel selectedKey={selectedKey} /></aside>;
}
